import { Box, Button, Chip, Typography } from "@mui/material";
import React from "react";
import { AppModalDialogBox } from "./modals";
import {
  concatSelectedFromToTime,
  formatYYYYMMDDDate,
  getGroupTimesByDate,
} from "../services/date-and-time.utils";
import { CalendarToday, CheckCircle } from "@mui/icons-material";

export default function SlotListDetails({
  times,
  selectedSlot,
  isCalendly,
  onClose,
}) {
  function renderAvailableTimeSlots() {
    if (times?.length) {
      return <SuggestedSlots selectedSlot={selectedSlot} times={times} />;
    }

    if (isCalendly) {
      return (
        <Typography
          fontSize={13}
          textAlign={"center"}
          mt={2}
          mb={2}
          color={"primary.main"}
        >
          Times suggested via Calendly
        </Typography>
      );
    }
  }

  return (
    <AppModalDialogBox
      isModalOpen={true}
      title="Times"
      handleClose={onClose}
      size={"md"}
      actionButtonRenderer={<Button onClick={onClose}>Close</Button>}
    >
      {selectedSlot && <SelectedSlot slot={selectedSlot} />}
      {renderAvailableTimeSlots()}
    </AppModalDialogBox>
  );
}

function SuggestedSlots({ selectedSlot, times }) {
  const groupedByDate = getGroupTimesByDate(times);
  const selectedDate = formatYYYYMMDDDate(selectedSlot);

  return (
    <Box sx={styles.slotsContainer}>
      {Object.keys(groupedByDate)?.map((date) => {
        const isSelectedDate = selectedDate === date;
        const slotsCount = groupedByDate[date]?.length || 0;

        return (
          <Box key={date} sx={styles.dateGroup}>
            <Box
              sx={[
                styles.dateHeader,
                isSelectedDate && styles.selectedDateHeader,
              ]}
            >
              <Box sx={styles.dateHeaderLeft}>
                <CalendarToday
                  sx={[
                    styles.dateIcon,
                    isSelectedDate && styles.selectedDateText,
                  ]}
                />
                <Typography
                  sx={[
                    styles.dateText,
                    isSelectedDate && styles.selectedDateText,
                  ]}
                >
                  {date}
                </Typography>
              </Box>
              <Box
                sx={[
                  styles.slotsCount,
                  isSelectedDate && styles.selectedSlotsCount,
                ]}
              >
                {slotsCount} slot{slotsCount !== 1 ? "s" : ""}
              </Box>
            </Box>

            <Box sx={styles.timeSlotsContainer}>
              <Box sx={styles.timeSlotsGrid}>
                {groupedByDate[date]?.map((timeSlot, index) => {
                  const isSelected = selectedSlot?.from === timeSlot?.from;
                  return (
                    <Chip
                      key={index}
                      label={concatSelectedFromToTime(timeSlot, true)}
                      size="small"
                      variant="outlined"
                      sx={[
                        styles.timeSlotChip,
                        isSelected && styles.selectedTimeSlotChip,
                      ]}
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function SelectedSlot({ slot }) {
  return (
    <Box sx={styles.selectedSlotSection}>
      <Box sx={styles.selectedSlotHeader}>
        <CheckCircle sx={styles.selectedSlotIcon} />
        <Typography sx={styles.selectedSlotTitle}>Selected Time:</Typography>
      </Box>
      <Typography sx={styles.selectedSlotTime}>
        {concatSelectedFromToTime(slot)}
      </Typography>
    </Box>
  );
}

const styles = {
  modalContent: {
    p: 2,
  },
  calendlyMessage: {
    textAlign: "center",
    py: 3,
    px: 3,
    borderRadius: 2,
    backgroundColor: "#f8f9fa",
    border: "1px solid #e0e0e0",
  },
  calendlyIcon: {
    fontSize: 32,
    color: "primary.main",
    mb: 1.5,
  },
  calendlyText: {
    color: "primary.main",
    fontWeight: 600,
    fontSize: 14,
  },
  slotsContainer: {
    maxHeight: 450,
    overflowY: "auto",
    border: "1px solid #e0e0e0",
    borderRadius: 2,
    backgroundColor: "#fafafa",
  },
  dateGroup: {
    borderBottom: "1px solid #e0e0e0",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  dateHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    p: 2,
    backgroundColor: "#f8f9fa",
    borderBottom: "1px solid #e0e0e0",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  dateHeaderLeft: {
    display: "flex",
    alignItems: "center",
  },
  dateIcon: {
    mr: 1.5,
    color: "text.secondary",
    fontSize: 18,
  },
  dateText: {
    fontWeight: 600,
    fontSize: 14,
    color: "text.primary",
  },
  slotsCount: {
    fontSize: 12,
    color: "text.secondary",
    backgroundColor: "#e0e0e0",
    px: 1.5,
    py: 0.5,
    borderRadius: 1,
  },
  selectedDateHeader: {
    backgroundColor: "#e8f5e8",
    borderColor: "#4caf50",
    "&:hover": {
      backgroundColor: "#d4edda",
    },
  },
  selectedDateText: {
    color: "success.main",
  },
  selectedSlotsCount: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  timeSlotsContainer: {
    p: 2,
    backgroundColor: "white",
  },
  timeSlotsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
  },
  timeSlotChip: {
    fontSize: 11,
    transition: "all 0.2s ease",
    border: "1px solid #e0e0e0",
    width: "fit-content",
  },
  selectedTimeSlotChip: {
    backgroundColor: "success.main",
    color: "white",
    borderColor: "success.main",
  },
  selectedSlotSection: {
    mb: 3,
  },
  selectedSlotHeader: {
    display: "flex",
    alignItems: "center",
    mb: 1,
  },
  selectedSlotIcon: {
    mr: 1,
    color: "success.main",
    fontSize: 16,
  },
  selectedSlotTitle: {
    fontWeight: 600,
    fontSize: 13,
    color: "success.main",
  },
  selectedSlotTime: {
    fontWeight: 500,
    fontSize: 12,
    color: "text.primary",
    ml: 2.5,
  },
  closeButton: {
    minWidth: 100,
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 13,
  },
};
